import L from 'leaflet';
import 'leaflet.markercluster';
import {loadPropertyMap} from './boundedPropertySearch';
import {privateListingMatches} from './chatPropertySearch';
import {searchQuery} from './propertySearchFilters';
import {clusterBubbleIcon,propertyMapIcon,mapPriceLabel} from './mapMarkerIcons';
export {clusterBubbleIcon} from './mapMarkerIcons';
const wrap=value=>((value+180)%360+360)%360-180;
export function viewportBounds(map) {
  const b=map.getBounds(),width=b.getEast()-b.getWest();
  return {north:Math.min(90,b.getNorth()),south:Math.max(-90,b.getSouth()),west:width>=360?-180:wrap(b.getWest()),east:width>=360?180:wrap(b.getEast())};
}
export function leafletBounds(bounds) {
  return [[bounds.south,bounds.west],[bounds.north,bounds.east<bounds.west?bounds.east+360:bounds.east]];
}
export function bindPropertyPreview(marker,map,feature,getPreview) {
  if(!getPreview)return;
  let hovered=false,generation=0;
  const clear=()=>{hovered=false;generation++;marker.unbindTooltip();};
  marker.on('mouseover',async()=>{
    hovered=true;const request=++generation;
    try {
      const imageUrl=await getPreview(feature);
      if(!imageUrl||!hovered||request!==generation||!map.hasLayer(marker))return;
      const image=document.createElement('img');image.src=imageUrl;image.alt='Property photo preview';image.className='thumbnail-tooltip-image';
      marker.bindTooltip(image,{direction:'top',offset:[0,-10],className:'thumbnail-tooltip',pane:map.getPane('searchLensPreviewPane')?'searchLensPreviewPane':'tooltipPane'}).openTooltip();
    }catch { /* A missing photo must not prevent opening the property. */ }
  });
  marker.on('mouseout',clear);marker.on('remove',clear);marker.on('click',clear);
}

export function renderBoundedFeatures(map,features,{onProperty,getPreview,params={},privateRows=[]}={}) {
  // Cluster only the bounded response in the browser. Aggregate markers carry their
  // listing count so a bubble represents homes, not the number of server cells.
  const layer=L.markerClusterGroup({maxClusterRadius:50,spiderfyOnMaxZoom:true,showCoverageOnHover:false,zoomToBoundsOnClick:true,
    iconCreateFunction:cluster=>clusterBubbleIcon(cluster.getAllChildMarkers().reduce((sum,marker)=>sum+marker.options.listingCount,0))});
  for(const feature of [...features,...privateRows]) {
    const lat=Number(feature.latitude),lng=Number(feature.longitude);
    if(!Number.isFinite(lat)||!Number.isFinite(lng))continue;
    const cluster=feature.kind==='cluster';
    const amount=feature.current_price??feature.list_price;
    const icon=cluster?clusterBubbleIcon(feature.count):propertyMapIcon(amount,{isPrivate:feature.isPrivate});
    const marker=L.marker([lat,lng],{icon,listingCount:cluster?Number(feature.count):1,title:cluster?`${Number(feature.count).toLocaleString()} homes`:`${mapPriceLabel(amount)} home`});
    if(cluster) {
      const content=document.createElement('div');const labelNode=document.createElement('p');labelNode.textContent=`${Number(feature.count).toLocaleString()} homes in this area`;content.appendChild(labelNode);
      const link=document.createElement('a');link.textContent='View homes in this area';
      // Coincident locations still have an accessible paged list when further zoom cannot separate them.
      const b=feature.bounds;const area={north:Math.min(90,b.north+0.000001),south:Math.max(-90,b.south-0.000001),east:b.east+0.000001>180?-180+(b.east+0.000001-180):b.east+0.000001,west:b.west-0.000001< -180?180+(b.west-0.000001+180):b.west-0.000001};
      link.href=`/properties/list?${searchQuery({...params,...area})}`;content.appendChild(link);marker.bindPopup(content);
      marker.on('click',()=>{map.fitBounds(leafletBounds(area),{maxZoom:Math.min(18,map.getZoom()+2),padding:[35,35]});marker.openPopup();});
    } else {
      bindPropertyPreview(marker,map,feature,getPreview);
      marker.on('click',()=>onProperty?.(feature));
    }
    layer.addLayer(marker);
  }
  layer.addTo(map);return layer;
}

export function attachBoundedMap(map,{params,initialMap,privateRows=[],onProperty,getPreview,onStatus,fetchImpl=fetch}) {
  let layer,controller,timer,disposed=false,generation=0;
  const paint=(data,bounds)=>{
    if(layer)map.removeLayer(layer);
    const visiblePrivate=privateRows.filter(row=>privateListingMatches(row,{...params,...bounds}));
    layer=renderBoundedFeatures(map,data.features,{onProperty,getPreview,params:{...params,...bounds},privateRows:visiblePrivate});
    onStatus?.({loading:false,error:'',count:data.match_count+visiblePrivate.length,bounds});
  };
  const privateCoords=privateRows.filter(row=>row.latitude!=null&&row.longitude!=null&&Number.isFinite(Number(row.latitude))&&Number.isFinite(Number(row.longitude))).map(row=>[Number(row.latitude),Number(row.longitude)]);
  const fitPoints=initialMap.bounds?leafletBounds(initialMap.bounds):[];
  privateCoords.forEach(([lat,lng])=>fitPoints.push([lat,initialMap.bounds?.west>initialMap.bounds?.east&&lng<initialMap.bounds.west?lng+360:lng]));
  if(fitPoints.length)map.fitBounds(fitPoints,{maxZoom:15,padding:[40,40],animate:false});
  paint(initialMap,viewportBounds(map));
  const refresh=async()=>{
    const request=++generation;controller?.abort();controller=new AbortController();const bounds=viewportBounds(map);
    onStatus?.({loading:true,error:'',bounds});
    try {
      const result=await loadPropertyMap({params,bounds,zoom:map.getZoom(),fetchImpl,signal:controller.signal});
      if(!disposed&&request===generation)paint(result.map,bounds);
    }catch(error){if(!disposed&&request===generation&&error.name!=='AbortError')onStatus?.({loading:false,error:error.message,bounds});}
  };
  const schedule=()=>{clearTimeout(timer);controller?.abort();generation++;timer=setTimeout(refresh,350);};
  map.on('moveend',schedule);schedule();
  return ()=>{disposed=true;generation++;clearTimeout(timer);controller?.abort();map.off('moveend',schedule);if(layer)map.removeLayer(layer);};
}
